import type { CampaignPageTemplate, CampaignTargeting } from "./types";

interface SiteRoutes {
  venueDetail?: string;
}

const STATIC_PATHS = new Set([
  "/ekle",
  "/ucretsiz-teklif-al",
  "/hakkimizda",
  "/iletisim",
  "/kullanim-kosullari",
  "/yasal-uyari",
  "/site-haritasi",
]);

export function normalizePath(pathname: string): string {
  if (!pathname) return "/";
  let path = pathname.split(/[?#]/)[0] || "/";
  try {
    path = decodeURIComponent(path);
  } catch {
    // Bozuk kodlamada yolu olduğu gibi kullan.
  }
  path = path.toLocaleLowerCase("tr");
  if (path.length > 1) path = path.replace(/\/+$/, "");
  return path || "/";
}

/** Sayfanın hangi şablona ait olduğunu yoldan çıkarır. */
export function getPageTemplate(pathname: string, routes?: SiteRoutes | null): CampaignPageTemplate | null {
  const path = normalizePath(pathname);
  if (path === "/") return "home";

  const segments = path.split("/").filter(Boolean);
  const [first] = segments;

  if (first === "sirket-indirimleri") return "company-discounts";
  if (first === "kampanyalar") return "campaigns";
  if (first === "etkinlikler") return "events";
  if (first === "hesabim") return "account";
  if (first === "blog") return segments.length > 1 ? "blog-detail" : "blog";
  if (STATIC_PATHS.has(`/${first}`)) return "static";
  if (routes?.venueDetail && first === routes.venueDetail.toLocaleLowerCase("tr") && segments.length > 1) {
    return "venue";
  }
  if (segments.length === 1) return "list";
  return null;
}

/**
 * `*` joker karakter destekli yol eşleşmesi. `/blog/*` hem `/blog/x` hem
 * `/blog/x/y` ile eşleşir; joker içermeyen kural yalnızca aynı yolla eşleşir.
 */
export function matchesPathRule(pathname: string, rule: string): boolean {
  const path = normalizePath(pathname);
  const normalizedRule = normalizePath(rule.trim());
  if (!normalizedRule.includes("*")) return path === normalizedRule;

  const pattern = normalizedRule
    .split("*")
    .map((part) => part.replace(/[.+?^${}()|[\]\\]/g, "\\$&"))
    .join(".*");
  return new RegExp(`^${pattern}$`).test(path);
}

/** Hariç kuralı her zaman önceliklidir. */
export function isPathTargeted(
  pathname: string,
  targeting: CampaignTargeting | null | undefined,
  routes?: SiteRoutes | null,
): boolean {
  if (!targeting) return false;
  if ((targeting.excluded ?? []).some((rule) => matchesPathRule(pathname, rule))) return false;
  if (targeting.mode === "all") return true;

  const template = getPageTemplate(pathname, routes);
  return (
    (targeting.pages ?? []).some((page) => matchesPathRule(pathname, page)) ||
    (template !== null && (targeting.templates ?? []).includes(template)) ||
    (targeting.patterns ?? []).some((rule) => matchesPathRule(pathname, rule))
  );
}
